import React from 'react';

import { Field, FormikErrors } from 'formik';

import { IconButton, MenuItem } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';

import {
    MobileDatePicker,
    MobileDateTimePicker,
    MobileTimePicker,
} from '@mui/x-date-pickers';
import { Box } from '@mui/material';

import TranslationHelper from '../../../../helpers/TranslationHelper';

import FieldWrapper from '../../../../components/common/formikWrappers/FieldWrapper';

import { getCommonDateTimeProps } from './utils';
import { useStyles } from './Themable.hooks';
import { InputComponent } from './InputComponent';
import { TAttributeDictionary, TMappedAttribute } from '../../types';
import { ATTRIBUTE_TYPE_VALUES, ATTRIBUTE_TYPES } from '../../constants';

interface IOwnProps {
    attributes: TAttributeDictionary;
    errors?: FormikErrors<TAttributeDictionary>;
    setFieldValue: (fieldname: string, v: any) => void;
}

const AttributesForm = ({ attributes, errors, setFieldValue }: IOwnProps) => {
    const classes = useStyles();

    const sortedAttributes = Object.values(attributes)
        .map((attr) => ({
            ...attr,
            translatedLabel:
                attr.internal === true
                    ? TranslationHelper.translate(attr.label)
                    : attr.label,
        }))
        .sort((a, b) => a.translatedLabel.localeCompare(b.translatedLabel));

    const getAttributeField = (attribute: TMappedAttribute) => {
        const { label, regex, name, type, required, value } = attribute;

        const fullLabel = `${
            attribute.internal === true
                ? TranslationHelper.translate(label)
                : label
        }${regex ? ` (regex: ${regex}, ` : ' ('}${TranslationHelper.translate(
            'Type'
        )}: ${TranslationHelper.translate(ATTRIBUTE_TYPES[type])})`;
        const error = errors?.[`${attribute?.name}.value`] as string;
        const commonDateTimeProps = getCommonDateTimeProps(
            attribute,
            fullLabel,
            error,
            setFieldValue
        );
        const attrTypeName = ATTRIBUTE_TYPES[type];
        switch (attrTypeName) {
            case ATTRIBUTE_TYPE_VALUES.String:
            case ATTRIBUTE_TYPE_VALUES.Integer:
            case ATTRIBUTE_TYPE_VALUES.Double:
                return (
                    <InputComponent
                        attribute={attribute}
                        error={error || ''}
                        setFieldValue={setFieldValue}
                        label={fullLabel}
                    />
                );

            case ATTRIBUTE_TYPE_VALUES.Boolean:
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                        }}
                    >
                        <Field
                            error={!!error}
                            helperText={error}
                            name={`attributes.${name}.value`}
                            type={'boolean'}
                            label={fullLabel}
                            required={required}
                            component={FieldWrapper}
                            select={true}
                            fullWidth
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                            ) =>
                                setFieldValue(
                                    `attributes.${name}.value`,
                                    e.target.value
                                )
                            }
                        >
                            <MenuItem key={'true'} value={'true'}>
                                {TranslationHelper.translate('true', 'wx')}
                            </MenuItem>
                            <MenuItem key={'false'} value={'false'}>
                                {TranslationHelper.translate('false', 'wx')}
                            </MenuItem>
                        </Field>
                        {value !== '' && (
                            <IconButton
                                onClick={() =>
                                    setFieldValue(
                                        `attributes.${name}.value`,
                                        ''
                                    )
                                }
                                size="small"
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                );

            case ATTRIBUTE_TYPE_VALUES.Date:
                return (
                    <MobileDatePicker
                        views={['year', 'month', 'day']}
                        {...commonDateTimeProps}
                    />
                );

            case ATTRIBUTE_TYPE_VALUES.Time:
                return (
                    <MobileTimePicker
                        views={['hours', 'minutes', 'seconds']}
                        {...commonDateTimeProps}
                    />
                );

            case ATTRIBUTE_TYPE_VALUES.DateTime:
                return (
                    <MobileDateTimePicker
                        views={[
                            'year',
                            'month',
                            'day',
                            'hours',
                            'minutes',
                            'seconds',
                        ]}
                        {...commonDateTimeProps}
                    />
                );

            default:
                return <div key={attribute.name} />;
        }
    };

    return (
        <div className={classes.paneContent}>
            {sortedAttributes.map((attribute) => {
                return (
                    <React.Fragment key={attribute.name}>
                        {getAttributeField(attribute)}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default AttributesForm;
